import React, { useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { Box } from "@mui/system";
import Menu from "./Menu";
import Header from "./Header";
import PermissionDeniedNotification from "./PermissionDeniedNotification";
import ChatWidget from "../chatBot/components/ChatWidget";
import SlidingServiceTab from "../../customer/serviceTracking/components/SlidingServiceTab";
import accountContext from "../../account/accountContext";
import NotificationToast from "../notifications/components/NotificationToast";
import { useFontTheme } from "../hooks/useFontTheme";
import { useAccentTheme, applyAccentTheme } from "../hooks/useAccentTheme";
import { ACCENT_THEME_STORAGE_KEY, DEFAULT_ACCENT_ID } from "../themes/accentThemes";
import customersApi from "../../customer/shared/api/customersApi";
import i18n from "../../core/i18n";
import usePermission from "../hooks/usePermission";
import { Modules } from "../../core/models/PermissionModels";
import { Roles } from "../../account/models/AccountModels";

const Layout = () => {
  const { pathname } = useLocation();
  const isAdmin = pathname.includes("admin");
  const isAuthenticated = Boolean(accountContext.user);
  // MIDAS_PILOT_ROLE_SPEC.md §5.4, AMENDED by owner directive 2026-08-19
  // (TAJMAC-PILOT-FE3): the pilot DOES get a header — reduced to the MIDAS button, the
  // language switcher and the user/avatar button (Header.tsx owns that reduction). The
  // original "suppress the header entirely" posture overshot: it left the pilot with no
  // way to switch language, reach their profile, or log out.
  //
  // Still suppressed here: the floating ChatWidget bubble (redundant inside full-screen
  // MIDAS) and the admin sidebar Menu (one-frame paint guard).
  // SlidingServiceTab needs no extra gate — it already requires Servicing access and the
  // pilot is seeded Servicing:0 (verified: usePermission.hasAccess = level >= Read).
  // PermissionDeniedNotification + NotificationToast stay (harmless, no special-casing).
  const isMidasPilot = accountContext.user?.role === Roles.MIDAS_PILOT;
  const { syncFromServer } = useFontTheme();
  const { syncFromServer: syncAccentFromServer } = useAccentTheme();
  const { hasAccess: hasServicingAccess } = usePermission(Modules.Servicing);

  // Apply accent theme from localStorage immediately on mount (before API call)
  useEffect(() => {
    try {
      const stored = localStorage.getItem(ACCENT_THEME_STORAGE_KEY);
      applyAccentTheme(stored || DEFAULT_ACCENT_ID);
    } catch {
      applyAccentTheme(DEFAULT_ACCENT_ID);
    }
  }, []);

  // Sync font + accent themes + language from Account service after login
  useEffect(() => {
    if (isAuthenticated) {
      customersApi.getAccount().then((account) => {
        if (account?.fontTheme) syncFromServer(account.fontTheme);
        if (account?.accentTheme) syncAccentFromServer(account.accentTheme);
        // Sync language preference: 1=en, 3=cs, 4=sk
        if (account?.languageId) {
          const languageIdToLocale: Record<number, string> = { 1: "en", 3: "cs", 4: "sk" };
          const locale = languageIdToLocale[account.languageId];
          if (locale && locale !== i18n.language) {
            i18n.changeLanguage(locale);
          }
        }
      }).catch(() => {
        // silent -- localStorage is the fallback for all preferences
      });
    }
  }, [isAuthenticated, syncFromServer, syncAccentFromServer]);

  return (
    <>
      {/* `!isMidasPilot` closes a one-frame paint (2026-08-19, TAJMAC-PILOT review F6):
          PilotRouteGuard redirects an /admin URL away, but <Navigate> acts in an
          effect, so the first commit would already have painted the admin sidebar. */}
      {isAdmin && !isMidasPilot && <Menu />}

      <Box className={`w-full ${isAdmin ? "v-panel" : "v-panel-no-sidebar"}`}>
        <Header />
        <Outlet />
      </Box>

      <PermissionDeniedNotification />
      <NotificationToast />
      {isAuthenticated && !isAdmin && hasServicingAccess && <SlidingServiceTab />}
      {!isMidasPilot && <ChatWidget />}
    </>
  );
};

export default Layout;
